import { Updater } from '@tanstack/vue-table'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { type Ref, toValue } from 'vue'
import axios from 'axios'
import dayjs from 'dayjs'
import { Category } from '@/lib/schema/v2/category'
import { Company } from '@/lib/schema/v2/company'
import { ZodObject } from 'zod'
import { TicketStatus } from '@/lib/schema/v2/ticket-status'
import { Ticket } from '@/lib/schema/v2/ticket'
import { z } from 'zod/v4'
import { CompanyType } from '@/lib/schema/v2/company-type'
import { CompanyBillingType } from '@/lib/schema/v2/company-billing-type'
import { Conversation } from '@/lib/schema/v2/conversation'
import { TicketPriority } from '@/lib/schema/v2/ticket-priority'
import { TicketType } from '@/lib/schema/v2/ticket-type'
import { TicketDepartment } from '@/lib/schema/v2/ticket-department'
import { CompanyContact } from '@/lib/schema/v2/company-contact'
import { Staff } from '@/lib/schema/v2/staff'
import { TicketResource } from '@/lib/schema/v2/ticket-resource'
import { TaxRate } from '@/lib/schema/v2/tax-rate'
import { ServiceGroup } from '@/types'
import { Article } from '@/lib/schema/v2/article'
import { Tenant } from '@/lib/schema/v2/tenant'
import { Datum } from '@/lib/schema/v2/datum'
import { Tariff } from '@/lib/schema/v2/tariff'
import { Rate } from '@/lib/schema/v2/rate'
import { MicrosoftUser } from '@/lib/schema/v2/microsoft-user'
import { CompanyService } from '@/lib/schema/v2/company-service'
import { CompanyAsset } from '@/lib/schema/v2/company-asset'
import { Role } from '@/lib/schema/v2/role'
import { CompanyLocation } from '@/lib/schema/v2/company-location'
import { Timezone } from '@/lib/schema/v2/timezone'

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export function clean_query_keys(keys: unknown[]) {
    return keys.filter((key) => isString(toValue(key)))
}
/**
 * Checks if a value is empty
 * - null or undefined: true
 * - string: true if length is 0 or only whitespace
 * - array: true if length is 0
 * - object: true if has no enumerable properties
 * - Map/Set: true if size is 0
 * - number: false (including 0)
 * - boolean: false
 */
export function isEmpty(value: unknown): boolean {
    // null or undefined
    if (value == null) {
        return true
    }

    // string
    if (typeof value === 'string') {
        return value.trim().length === 0
    }

    // array
    if (Array.isArray(value)) {
        return value.length === 0
    }

    // Map or Set
    if (value instanceof Map || value instanceof Set) {
        return value.size === 0
    }

    // object (plain objects, class instances, etc.)
    if (typeof value === 'object') {
        return Object.keys(value).length === 0
    }

    // primitives (number, boolean, symbol, bigint, function)
    return false
}

export function isUuid(value: unknown): value is string {
    const response = z.uuid().safeParse(value)

    return response.success
}

export function isString(value: unknown): value is string {
    return typeof value === 'string'
}

export function isNumber(value: unknown): value is number {
    return typeof value === 'number' && !isNaN(value)
}

export function isBoolean(value: unknown): value is boolean {
    return typeof value === 'boolean'
}

export function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

export function isArray<T>(value: unknown): value is T[] {
    return Array.isArray(value)
}

export function isArticle(value: unknown): value is Article {
    return isObject(value) && 'uuid' in value
}

export function isCategory(value: unknown): value is Category {
    return isObject(value) && 'uuid' in value
}

export function isCompanyAsset(value: unknown): value is CompanyAsset {
    return isObject(value) && 'uuid' in value
}

export function isCompanyService(value: unknown): value is CompanyService {
    return isObject(value) && 'uuid' in value
}

export function isCompanyLocation(value: unknown): value is CompanyLocation {
    return isObject(value) && 'uuid' in value
}

export function isMicrosoftUser(value: unknown): value is MicrosoftUser {
    return isObject(value) && 'uuid' in value
}

export function isRole(value: unknown): value is Role {
    return isObject(value) && 'uuid' in value
}

export function isTicketStatus(value: unknown): value is TicketStatus {
    return isObject(value) && 'uuid' in value
}

export function isTicketPriority(value: unknown): value is TicketPriority {
    return isObject(value) && 'uuid' in value
}

export function isTicketResource(value: unknown): value is TicketResource {
    return isObject(value) && 'uuid' in value
}

export function isTicketType(value: unknown): value is TicketType {
    return isObject(value) && 'uuid' in value
}

export function isTicketDepartment(value: unknown): value is TicketDepartment {
    return isObject(value) && 'uuid' in value
}

export function isTimezone(value: unknown): value is Timezone {
    return isObject(value) && 'uuid' in value
}

export function isConversation(value: unknown): value is Conversation {
    return isObject(value) && 'uuid' in value
}

export function isDatum(value: unknown): value is Datum {
    return isObject(value) && 'uuid' in value
}

export function isRate(value: unknown): value is Rate {
    return isObject(value) && 'uuid' in value
}

export function isTariff(value: unknown): value is Tariff {
    return isObject(value) && 'uuid' in value
}

export function isTaxRate(value: unknown): value is TaxRate {
    return isObject(value) && 'uuid' in value
}

export function isTenant(value: unknown): value is Tenant {
    return isObject(value) && 'uuid' in value
}

export function isTicket(value: unknown): value is Ticket {
    return isObject(value) && 'uuid' in value
}

export function isCompany(value: unknown): value is Company {
    return isObject(value) && 'uuid' in value
}

export function isCompanyContact(value: unknown): value is CompanyContact {
    return isObject(value) && 'uuid' in value
}

export function isStaff(value: unknown): value is Staff {
    return isObject(value) && 'uuid' in value
}

export function isCompanyType(value: unknown): value is CompanyType {
    return isObject(value) && 'uuid' in value
}

export function isCompanyBillingType(value: unknown): value is CompanyBillingType {
    return isObject(value) && 'name' in value
}

export function isServiceGroup(value: unknown): value is ServiceGroup {
    return isObject(value) && 'uuid' in value
}

export function valueUpdater<T extends Updater<any>>(updaterOrValue: T, ref: Ref) {
    ref.value = typeof updaterOrValue === 'function' ? updaterOrValue(ref.value) : updaterOrValue
}

export const getPropValue = (sourceObject: object, dotNotationPath: string) => {
    let returnData = sourceObject

    dotNotationPath.split('.').forEach((subPath) => {
        returnData = returnData ? returnData[subPath] : null

        if (dayjs.isDayjs(returnData)) {
            returnData = returnData.format('L') as string
        }
    })

    return returnData
}

export const parseAmpResponse = (data: unknown, schema: ZodObject) => {
    const response = schema.parse(data)

    if (!response.success) {
        throw new Error(response.error?.title, {
            cause: response.error,
        })
    }

    return response
}

export const setFieldsError = (
    error: unknown,
    setFieldError: (field: any, message: string | string[] | undefined) => void
) => {
    if ((axios.isAxiosError(error) && error.response?.status === 422) || error.status === 422) {
        const errors = error.response?.data?.errors ?? error.data?.errors

        if (errors) {
            Object.entries(errors).forEach(([key, value]) => {
                if (Array.isArray(value) && value.length > 0) {
                    setFieldError(key, value[0])
                }
            })
        }
    }
}

// Datatable page size for "All" options
export const tableAll = 999999999

export enum Entity {
    Article = 'article',
    Company = 'company',
    CompanyAsset = 'company_asset',
    CompanyContact = 'company_contact',
    CompanyService = 'company_service',
    Invoice = 'invoice',
    InvoiceItem = 'invoice_item',
    Payment = 'payment',
    Product = 'product',
    Rate = 'rate',
    Staff = 'staff',
    Ticket = 'ticket',
}

export enum EventType {
    ArticleCreated = 'article_created',
    ConversationCreated = 'conversation_created',
    ConversationItemCreated = 'conversation_item_created',
    TicketCreated = 'ticket_created',
    TicketResourceCreated = 'ticket_resource_created',
}

export const nameRegex = /^[a-z0-9]+(?:_[a-z0-9]+)*$/
